(() => {
  const initialize = (root) => {
    if (!root || root.dataset.lfsReady === 'true') return;
    root.dataset.lfsReady = 'true';

    const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
    const slides = [...root.querySelectorAll('[data-lfs-slide]')];
    const dots = [...root.querySelectorAll('[data-lfs-dot]')];
    const previousButton = root.querySelector('[data-lfs-previous]');
    const nextButton = root.querySelector('[data-lfs-next]');
    const hero = root.querySelector('[data-lfs-hero]');
    const viewport = hero?.querySelector('.lfs-hero__viewport');
    const nativeSwipeEnabled = Boolean(
      hero && viewport && root.closest('.shopify-section-lotus-feng-shui')
    );
    let currentIndex = Math.max(slides.findIndex((slide) => slide.classList.contains('is-active')), 0);
    let autoplayTimer;
    let scrollSyncTimer;
    let programmaticScroll = false;
    let pointerActive = false;
    let heroHovered = false;
    let heroFocused = false;

    const setSlide = (nextIndex, syncViewport = true, smooth = true) => {
      if (slides.length < 2) return;
      const previousIndex = currentIndex;
      currentIndex = (nextIndex + slides.length) % slides.length;

      slides.forEach((slide, index) => {
        const isCurrent = index === currentIndex;
        slide.classList.toggle('is-active', isCurrent);
        slide.toggleAttribute('inert', !isCurrent);
        slide.setAttribute('aria-hidden', String(!isCurrent));
      });

      dots.forEach((dot, index) => {
        if (index === currentIndex) dot.setAttribute('aria-current', 'true');
        else dot.removeAttribute('aria-current');
      });

      if (!nativeSwipeEnabled || !syncViewport) return;
      const targetLeft = slides[currentIndex].offsetLeft;
      if (Math.abs(viewport.scrollLeft - targetLeft) < 1) return;
      programmaticScroll = true;
      const shouldSmooth = smooth
        && !reduceMotion.matches
        && Math.abs(currentIndex - previousIndex) === 1;

      if (shouldSmooth) {
        viewport.scrollTo({ left: targetLeft, behavior: 'smooth' });
      } else {
        const inlineScrollBehavior = viewport.style.scrollBehavior;
        viewport.style.scrollBehavior = 'auto';
        viewport.scrollLeft = targetLeft;
        viewport.style.scrollBehavior = inlineScrollBehavior;
      }
    };

    const stopAutoplay = () => window.clearInterval(autoplayTimer);
    const startAutoplay = () => {
      stopAutoplay();
      if (!hero || slides.length < 2 || reduceMotion.matches) return;
      if (hero.dataset.autoplay !== 'true') return;
      if (document.hidden || pointerActive || heroHovered || heroFocused) return;
      const interval = Math.max(Number(hero.dataset.interval || 6), 3) * 1000;
      autoplayTimer = window.setInterval(() => setSlide(currentIndex + 1), interval);
    };

    dots.forEach((dot, index) => {
      dot.addEventListener('click', () => {
        setSlide(index);
        startAutoplay();
      });
    });

    previousButton?.addEventListener('click', () => {
      setSlide(currentIndex - 1);
      startAutoplay();
    });

    nextButton?.addEventListener('click', () => {
      setSlide(currentIndex + 1);
      startAutoplay();
    });

    if (nativeSwipeEnabled) {
      viewport.addEventListener('pointerdown', (event) => {
        if (!event.isPrimary) return;
        pointerActive = true;
        programmaticScroll = false;
        stopAutoplay();
      }, { passive: true });
      viewport.addEventListener('pointerup', () => {
        pointerActive = false;
        startAutoplay();
      }, { passive: true });
      viewport.addEventListener('pointercancel', () => {
        pointerActive = false;
        startAutoplay();
      }, { passive: true });
      viewport.addEventListener('scroll', () => {
        const wasProgrammatic = programmaticScroll;
        if (!wasProgrammatic) stopAutoplay();
        window.clearTimeout(scrollSyncTimer);
        scrollSyncTimer = window.setTimeout(() => {
          const nextIndex = slides.reduce((closest, slide, index) => {
            const distance = Math.abs(slide.offsetLeft - viewport.scrollLeft);
            return distance < closest.distance ? { distance, index } : closest;
          }, { distance: Number.POSITIVE_INFINITY, index: currentIndex }).index;

          if (nextIndex !== currentIndex) setSlide(nextIndex, false);
          if (wasProgrammatic) programmaticScroll = false;
          else startAutoplay();
        }, 120);
      }, { passive: true });
    }

    hero?.addEventListener('mouseenter', () => {
      heroHovered = true;
      stopAutoplay();
    });
    hero?.addEventListener('mouseleave', () => {
      heroHovered = false;
      startAutoplay();
    });
    hero?.addEventListener('focusin', () => {
      heroFocused = true;
      stopAutoplay();
    });
    hero?.addEventListener('focusout', (event) => {
      if (hero.contains(event.relatedTarget)) return;
      heroFocused = false;
      startAutoplay();
    });
    document.addEventListener('visibilitychange', () => {
      if (document.hidden) stopAutoplay();
      else startAutoplay();
    });

    if (slides.length > 1) {
      setSlide(currentIndex);
      startAutoplay();
    }

    const categoryButtons = [...root.querySelectorAll('[data-lfs-category]')];
    const intentionSelect = root.querySelector('[data-lfs-intention]');
    const intentionControl = root.querySelector('[data-lfs-intention-control]');
    const intentionTrigger = root.querySelector('[data-lfs-intention-trigger]');
    const intentionLabel = root.querySelector('[data-lfs-intention-label]');
    const intentionOptions = root.querySelector('[data-lfs-intention-options]');
    const intentionOptionButtons = [...root.querySelectorAll('[data-lfs-intention-option]')];
    const productCards = [...root.querySelectorAll('[data-lfs-product]')];
    const productGrid = root.querySelector('[data-lfs-product-grid]');
    const emptyState = root.querySelector('[data-lfs-empty]');
    const loadingState = root.querySelector('[data-lfs-loading]');
    let selectedCategory = 'all';
    let productsAreLoading = Boolean(root.querySelector('[data-lfs-next-page]'));
    let productsAreComplete = !productsAreLoading;

    const normalize = (value) => String(value || '').trim().toLowerCase();
    const valuesFrom = (value) =>
      normalize(value)
        .split('|')
        .map((item) => item.trim())
        .filter(Boolean);
    const aliasesFrom = (value) =>
      normalize(value)
        .split(',')
        .map((item) => item.trim())
        .filter(Boolean);
    const baseValue = (value) =>
      normalize(value)
        .replace(/\s*[\(（].*$/, '')
        .trim();
    const includesValue = (source, value) => {
      const selectedValues = aliasesFrom(value);
      if (!selectedValues.length || selectedValues.includes('all')) return true;

      return valuesFrom(source).some((sourceValue) => {
        const sourceBaseValue = baseValue(sourceValue);
        return selectedValues.some(
          (selectedValue) =>
            sourceValue === selectedValue || sourceBaseValue === selectedValue,
        );
      });
    };

    const updateFilterAvailability = (selectedIntention) => {
      categoryButtons.forEach((button) => {
        const value = normalize(button.dataset.lfsCategory) || 'all';
        const disabled = productsAreComplete && value !== 'all' && value !== selectedCategory
          && !productCards.some((card) =>
            includesValue(card.dataset.productTypes || card.dataset.productType, value)
            && includesValue(card.dataset.productIntentions || card.dataset.productTags, selectedIntention),
          );
        button.disabled = disabled;
        button.setAttribute('aria-disabled', String(disabled));
      });

      const intentionIsUnavailable = (value) => productsAreComplete
        && value !== 'all' && value !== selectedIntention
        && !productCards.some((card) =>
          includesValue(card.dataset.productTypes || card.dataset.productType, selectedCategory)
          && includesValue(card.dataset.productIntentions || card.dataset.productTags, value),
        );

      [...(intentionSelect?.options || [])].forEach((option) => {
        option.disabled = intentionIsUnavailable(normalize(option.value) || 'all');
      });
      intentionOptionButtons.forEach((option) => {
        option.disabled = intentionIsUnavailable(normalize(option.dataset.lfsIntentionOption) || 'all');
        option.setAttribute('aria-disabled', String(option.disabled));
      });
    };

    const filterProducts = () => {
      const selectedIntention = normalize(intentionSelect?.value || 'all');
      let visibleCount = 0;

      productCards.forEach((card) => {
        const productTypes = card.dataset.productTypes || card.dataset.productType || '';
        const intentions = card.dataset.productIntentions || card.dataset.productTags || '';
        const categoryMatches = selectedCategory === 'all'
          || includesValue(productTypes, selectedCategory);
        const intentionMatches = !selectedIntention
          || selectedIntention === 'all'
          || includesValue(intentions, selectedIntention);
        const shouldShow = categoryMatches && intentionMatches;

        card.hidden = !shouldShow;
        if (shouldShow) visibleCount += 1;
      });

      if (emptyState) emptyState.hidden = productsAreLoading || visibleCount !== 0;
      updateFilterAvailability(selectedIntention);
    };

    categoryButtons.forEach((button) => {
      button.addEventListener('click', () => {
        if (button.disabled) return;
        selectedCategory = normalize(button.dataset.lfsCategory) || 'all';
        categoryButtons.forEach((candidate) => {
          const isActive = candidate === button;
          candidate.classList.toggle('is-active', isActive);
          candidate.setAttribute('aria-pressed', String(isActive));
        });
        filterProducts();
      });
    });

    const closeIntentionOptions = () => {
      if (!intentionTrigger || !intentionOptions) return;
      intentionTrigger.setAttribute('aria-expanded', 'false');
      intentionOptions.hidden = true;
    };

    const openIntentionOptions = (focusSelected = false) => {
      if (!intentionTrigger || !intentionOptions) return;
      intentionTrigger.setAttribute('aria-expanded', 'true');
      intentionOptions.hidden = false;
      if (focusSelected) {
        const enabledOptions = intentionOptionButtons.filter((option) => !option.disabled);
        (enabledOptions.find((option) => option.classList.contains('is-selected')) || enabledOptions[0])?.focus();
      }
    };

    const selectIntention = (value, label) => {
      const normalizedValue = normalize(value) || 'all';
      if (intentionSelect) intentionSelect.value = normalizedValue;
      if (intentionLabel) intentionLabel.textContent = label || intentionSelect?.selectedOptions?.[0]?.textContent || '';

      intentionOptionButtons.forEach((option) => {
        const isSelected = normalize(option.dataset.lfsIntentionOption) === normalizedValue;
        option.classList.toggle('is-selected', isSelected);
        option.setAttribute('aria-selected', String(isSelected));
      });

      filterProducts();
    };

    intentionTrigger?.addEventListener('click', () => {
      if (intentionTrigger.getAttribute('aria-expanded') === 'true') closeIntentionOptions();
      else openIntentionOptions();
    });

    intentionTrigger?.addEventListener('keydown', (event) => {
      if (event.key !== 'ArrowDown' && event.key !== 'ArrowUp') return;
      event.preventDefault();
      openIntentionOptions(true);
    });

    intentionOptionButtons.forEach((option) => {
      option.addEventListener('click', () => {
        if (option.disabled) return;
        selectIntention(option.dataset.lfsIntentionOption, option.textContent.trim());
        closeIntentionOptions();
        intentionTrigger?.focus();
      });

      option.addEventListener('keydown', (event) => {
        const enabledOptions = intentionOptionButtons.filter((candidate) => !candidate.disabled);
        const index = enabledOptions.indexOf(option);
        let nextIndex = index;
        if (event.key === 'ArrowDown') nextIndex = (index + 1) % enabledOptions.length;
        else if (event.key === 'ArrowUp') nextIndex = (index - 1 + enabledOptions.length) % enabledOptions.length;
        else if (event.key === 'Home') nextIndex = 0;
        else if (event.key === 'End') nextIndex = enabledOptions.length - 1;
        else if (event.key === 'Escape') {
          event.preventDefault();
          closeIntentionOptions();
          intentionTrigger?.focus();
          return;
        } else return;

        event.preventDefault();
        enabledOptions[nextIndex]?.focus();
      });
    });

    intentionSelect?.addEventListener('change', () => {
      const selectedOption = intentionOptionButtons.find((option) => normalize(option.dataset.lfsIntentionOption) === normalize(intentionSelect.value));
      selectIntention(intentionSelect.value, selectedOption?.textContent.trim());
    });

    const handleOutsideClick = (event) => {
      if (!intentionControl?.contains(event.target)) closeIntentionOptions();
    };
    document.addEventListener('click', handleOutsideClick);

    const loadRemainingProducts = async () => {
      if (!productGrid) return;
      let nextUrl = root.querySelector('[data-lfs-next-page]')?.dataset.lfsNextPage;
      const visitedPages = new Set();

      try {
        while (nextUrl) {
          const url = new URL(nextUrl, window.location.origin);
          url.searchParams.set('section_id', root.dataset.lfsRoot);
          if (visitedPages.has(url.toString())) throw new Error('Repeated collection page');
          visitedPages.add(url.toString());
          const response = await fetch(url.toString(), { headers: { 'X-Requested-With': 'XMLHttpRequest' } });
          if (!response.ok) throw new Error(`Unable to load products (${response.status})`);

          const documentFragment = new DOMParser().parseFromString(await response.text(), 'text/html');
          const nextRoot = documentFragment.querySelector('[data-lfs-root]');
          if (nextRoot?.dataset.lfsRoot !== root.dataset.lfsRoot || !nextRoot.querySelector('[data-lfs-product-grid]')) {
            throw new Error('Collection page is missing its product grid');
          }

          const newCards = [...nextRoot.querySelectorAll('[data-lfs-product]')];
          if (!newCards.length) throw new Error('Collection page did not include products');
          newCards.forEach((card) => {
            card.classList.add('is-revealed');
            productGrid.append(card);
            productCards.push(card);
          });

          nextUrl = nextRoot.querySelector('[data-lfs-next-page]')?.dataset.lfsNextPage || '';
        }
        productsAreComplete = true;
      } catch (error) {
        console.warn('[Lotus Feng Shui] Complete collection loading stopped.', error);
      } finally {
        productsAreLoading = false;
        if (loadingState) loadingState.hidden = true;
        filterProducts();
      }
    };

    selectIntention(intentionSelect?.value || 'all', intentionOptionButtons[0]?.textContent.trim());
    loadRemainingProducts();

    const revealItems = [...root.querySelectorAll('[data-lfs-reveal]')];
    if (!reduceMotion.matches && 'IntersectionObserver' in window && revealItems.length) {
      root.classList.add('lfs-motion-ready');
      revealItems.forEach((item, index) => {
        item.classList.add('lfs-reveal');
        item.style.setProperty('--lfs-delay', `${Math.min(index % 4, 3) * 70}ms`);
      });

      const observer = new IntersectionObserver((entries) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting) return;
          entry.target.classList.add('is-revealed');
          observer.unobserve(entry.target);
        });
      }, { rootMargin: '0px 0px -8% 0px', threshold: 0.08 });

      revealItems.forEach((item) => observer.observe(item));
      root._lfsObserver = observer;
    }

    root._lfsCleanup = () => {
      stopAutoplay();
      window.clearTimeout(scrollSyncTimer);
      root._lfsObserver?.disconnect();
      document.removeEventListener('click', handleOutsideClick);
    };
  };

  const initializeAll = (scope = document) => {
    scope.querySelectorAll('[data-lfs-root]').forEach(initialize);
  };

  initializeAll();

  document.addEventListener('shopify:section:load', (event) => initializeAll(event.target));
  document.addEventListener('shopify:section:unload', (event) => {
    event.target.querySelectorAll('[data-lfs-root]').forEach((root) => root._lfsCleanup?.());
  });
})();
